import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus,
  Zap,
  Loader2,
  Pencil,
  Trash2,
  CalendarRange,
  Timer,
  CheckCircle2,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface Props {
  baProjectId: string | null;
}

interface Sprint {
  id: string;
  name: string;
  goal: string;
  start_date: string;
  end_date: string;
  status: "planned" | "active" | "completed" | "cancelled";
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  planned: "bg-gray-100 text-gray-600 border-gray-300",
  active: "bg-blue-100 text-blue-800 border-blue-300",
  completed: "bg-green-100 text-green-800 border-green-300",
  cancelled: "bg-red-100 text-red-700 border-red-300",
};

const STATUS_DOT: Record<string, string> = {
  planned: "bg-gray-400",
  active: "bg-blue-500",
  completed: "bg-green-500",
  cancelled: "bg-red-400",
};

function daysBetween(a: string, b: string): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round(
    (new Date(b).getTime() - new Date(a).getTime()) / msPerDay
  );
}

function daysRemaining(end: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil(
    (new Date(end).getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
}

function formatDateRange(start: string, end: string): string {
  const fmt = (d: string) =>
    new Date(d).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
    });
  return `${fmt(start)} – ${fmt(end)}`;
}

const EMPTY_FORM = {
  name: "",
  goal: "",
  start_date: "",
  end_date: "",
  status: "planned" as Sprint["status"],
};

export default function SprintPlanningPanel({ baProjectId }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    if (baProjectId) load();
    else setSprints([]);
  }, [baProjectId]);

  const load = async () => {
    if (!baProjectId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/api/tools/sprints?project_id=${baProjectId}`,
        { headers: authHeader }
      );
      setSprints(res.data || []);
    } catch (e) {
      console.error("Failed to load sprints", e);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const openEdit = (s: Sprint) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      goal: s.goal,
      start_date: s.start_date ? s.start_date.split("T")[0] : "",
      end_date: s.end_date ? s.end_date.split("T")[0] : "",
      status: s.status,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${BASE}/api/tools/sprints/${editingId}`, form, {
          headers: authHeader,
        });
      } else {
        await axios.post(
          `${BASE}/api/tools/sprints`,
          { ...form, project_id: baProjectId },
          { headers: authHeader }
        );
      }
      setShowForm(false);
      setForm({ ...EMPTY_FORM });
      setEditingId(null);
      load();
    } catch (e) {
      console.error("Failed to save sprint", e);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await axios.delete(`${BASE}/api/tools/sprints/${id}`, {
        headers: authHeader,
      });
      setDeleteConfirmId(null);
      load();
    } catch (e) {
      console.error("Failed to delete sprint", e);
    }
  };

  // Sort: active first, then by start_date
  const sorted = [...sprints].sort((a, b) => {
    const order = { active: 0, planned: 1, completed: 2, cancelled: 3 };
    if (order[a.status] !== order[b.status])
      return order[a.status] - order[b.status];
    return (a.start_date || "").localeCompare(b.start_date || "");
  });

  if (!baProjectId) {
    return (
      <div className="h-full flex items-center justify-center text-[#5C5C57]">
        <p className="text-sm">No project selected.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-[#314E6E]" />
          <h2 className="text-lg font-semibold text-[#1E1E1C]">
            Sprint Planning
          </h2>
          <span className="text-xs text-[#5C5C57] bg-[#F7F4EE] border border-[#D9D4CC] rounded-full px-2 py-0.5">
            {sprints.length}
          </span>
        </div>
        <Button
          size="sm"
          onClick={openNew}
          className="bg-[#314E6E] hover:bg-[#243b54] text-white gap-1"
        >
          <Plus className="h-4 w-4" />
          Add Sprint
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-[#314E6E]" />
        </div>
      )}

      {/* Empty state */}
      {!loading && sprints.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-[#5C5C57]">
          <Zap className="h-10 w-10 mb-3 opacity-30" />
          <p className="text-sm font-medium">No sprints planned yet.</p>
          <p className="text-xs mt-1 opacity-70">
            Plan and track your sprints here.
          </p>
        </div>
      )}

      {/* Sprint timeline cards */}
      {!loading && sorted.length > 0 && (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-5 top-4 bottom-4 w-0.5 bg-[#D9D4CC] rounded-full" />

          <div className="space-y-4">
            {sorted.map((sprint) => {
              const hasDates = sprint.start_date && sprint.end_date;
              const remaining =
                sprint.status === "active" && sprint.end_date
                  ? daysRemaining(sprint.end_date)
                  : null;
              const duration =
                sprint.status === "completed" && hasDates
                  ? daysBetween(sprint.start_date, sprint.end_date)
                  : null;

              return (
                <div key={sprint.id} className="flex gap-4 pl-2">
                  {/* Timeline dot */}
                  <div className="relative z-10 mt-4 shrink-0">
                    <div
                      className={`w-6 h-6 rounded-full border-2 border-white shadow-sm flex items-center justify-center ${STATUS_DOT[sprint.status]}`}
                    >
                      {sprint.status === "completed" && (
                        <CheckCircle2 className="h-3 w-3 text-white" />
                      )}
                    </div>
                  </div>

                  {/* Card */}
                  <div className="flex-1 bg-[#FCFBF8] border border-[#D9D4CC] rounded-xl p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-[#1E1E1C] text-sm">
                            {sprint.name}
                          </span>
                          <Badge
                            variant="outline"
                            className={`text-xs capitalize ${STATUS_COLORS[sprint.status]}`}
                          >
                            {sprint.status}
                          </Badge>
                        </div>

                        {hasDates && (
                          <div className="flex items-center gap-1.5 text-xs text-[#5C5C57] mb-2">
                            <CalendarRange className="h-3 w-3" />
                            {formatDateRange(
                              sprint.start_date,
                              sprint.end_date
                            )}
                          </div>
                        )}

                        {sprint.goal && (
                          <p className="text-sm text-[#5C5C57] leading-snug">
                            {sprint.goal}
                          </p>
                        )}

                        {/* Progress info */}
                        {(remaining !== null || duration !== null) && (
                          <div className="mt-3">
                            {remaining !== null && (
                              <div className="flex items-center gap-1.5 text-xs font-medium text-blue-700">
                                <Timer className="h-3.5 w-3.5" />
                                {remaining > 0
                                  ? `${remaining} day${remaining !== 1 ? "s" : ""} remaining`
                                  : remaining === 0
                                  ? "Ends today"
                                  : "Sprint overdue"}
                              </div>
                            )}
                            {duration !== null && (
                              <div className="flex items-center gap-1.5 text-xs font-medium text-green-700">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                Completed in {duration} day{duration !== 1 ? "s" : ""}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => openEdit(sprint)}
                          className="p-1.5 rounded-lg hover:bg-[#F7F4EE] text-[#5C5C57] hover:text-[#1E1E1C] transition-colors"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(sprint.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-[#5C5C57] hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg bg-[#FCFBF8] border border-[#D9D4CC]">
          <DialogHeader>
            <DialogTitle className="text-[#1E1E1C]">
              {editingId ? "Edit Sprint" : "Plan a Sprint"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Sprint Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Sprint 1"
                className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Sprint Goal
              </label>
              <Textarea
                value={form.goal}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                placeholder="What should this sprint achieve?"
                rows={3}
                className="border-[#D9D4CC] bg-white text-[#1E1E1C] resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) =>
                    setForm({ ...form, start_date: e.target.value })
                  }
                  className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  End Date
                </label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) =>
                    setForm({ ...form, end_date: e.target.value })
                  }
                  className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
                />
              </div>
            </div>
            {form.start_date && form.end_date && (
              <p className="text-xs text-[#5C5C57]">
                Duration:{" "}
                <span className="font-medium text-[#1E1E1C]">
                  {daysBetween(form.start_date, form.end_date)} days
                </span>
              </p>
            )}
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Status
              </label>
              <Select
                value={form.status}
                onValueChange={(v) =>
                  setForm({ ...form, status: v as Sprint["status"] })
                }
              >
                <SelectTrigger className="border-[#D9D4CC] bg-white text-[#1E1E1C]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(false)}
              className="border-[#D9D4CC] text-[#5C5C57]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving || !form.name.trim()}
              className="bg-[#314E6E] hover:bg-[#243b54] text-white"
            >
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {editingId ? "Save Changes" : "Create Sprint"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteConfirmId}
        onOpenChange={() => setDeleteConfirmId(null)}
      >
        <DialogContent className="max-w-sm bg-[#FCFBF8] border border-[#D9D4CC]">
          <DialogHeader>
            <DialogTitle className="text-[#1E1E1C]">Delete Sprint?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C5C57] mt-1">
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteConfirmId(null)}
              className="border-[#D9D4CC] text-[#5C5C57]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => deleteConfirmId && remove(deleteConfirmId)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
